import { useMemo } from 'react';

import type { MaintenanceListTableBriefData } from '@/components/sections/service/maintenance-list/table/brief';
import { MaintenanceListTableBrief } from '@/components/sections/service/maintenance-list/table/brief';
import type { MaintenanceListTableFullData } from '@/components/sections/service/maintenance-list/table/full';
import { MaintenanceListTableFull } from '@/components/sections/service/maintenance-list/table/full';
import { MaintenanceListTableHeader } from '@/components/sections/service/maintenance-list/table/header';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import type { IVehicleType } from '@/types/services/contentstack';
import type {
  ContentstackAllVehicleTypeMaintenanceOptionResponse,
  RegularMaintenaceSchedule,
} from '@/types/services/contentstack/services/maintenance-service';
import { IIcon } from '@/types/shared';

export type MaintenanceListTableDisplay = 'brief' | 'full';

type MaintenanceListTableItem = {
  key: string;
  labelTitle: string;
  labelIcon?: IIcon;
  valueText?: string;
  values?: boolean[];
};
export type MaintenanceListTableGroupItem = MaintenanceListTableItem & {
  isGroup?: boolean;
  children?: MaintenanceListTableGroupItem[];
};

export interface MaintenanceListPriceData {
  costBeforeVatTitle: string;
  discountTitle: string;
  totalCostWithVatTitle: string;
  costBeforeVatIcon: IIcon;
  discountIcon: IIcon;
  totalCostWithVatIcon: IIcon;
}
export interface MaintenanceListTableData {
  vehicleType?: IVehicleType;
  maintenanceSchedules?: RegularMaintenaceSchedule[];
  selectedIndex?: number;
  priceData?: MaintenanceListPriceData;
  selectedItem?: ContentstackAllVehicleTypeMaintenanceOptionResponse;
}

interface MaintenanceListTableProps {
  data?: MaintenanceListTableData;
  full?: MaintenanceListTableFullData;
  brief?: MaintenanceListTableBriefData;
  headerTitle?: string;
  tableDisplay?: MaintenanceListTableDisplay;
  onTableDisplayChange?: (status: MaintenanceListTableDisplay) => void;
}

export const MaintenanceListTable = ({
  tableDisplay,
  onTableDisplayChange,
  ...props
}: MaintenanceListTableProps) => {
  const filteredTableItems = useMemo<MaintenanceListTableGroupItem[]>(() => {
    if (props.data?.maintenanceSchedules?.length === 0) return [];

    const results: MaintenanceListTableGroupItem[] = [];
    const firstMaintenanceScheduleItem = props.data?.maintenanceSchedules?.[0];
    if (!firstMaintenanceScheduleItem) return [];
    const possibleGroupKeys = Object.keys(
      firstMaintenanceScheduleItem.maintenance_part_groups
    );

    for (const groupKey of possibleGroupKeys) {
      const groupValueByFirstMaintenanceScheduleItem =
        firstMaintenanceScheduleItem.maintenance_part_groups[groupKey];
      const labelTitle = groupValueByFirstMaintenanceScheduleItem.title;
      const partKeys =
        groupValueByFirstMaintenanceScheduleItem.maintenance_parts
          ? Object.keys(
              groupValueByFirstMaintenanceScheduleItem.maintenance_parts
            )
          : [];
      const isGroup = partKeys.length > 0;
      const children: MaintenanceListTableGroupItem[] = [];
      // Icon status to show in group row when tab closing
      const tmpGroupValues: boolean[] =
        props.data?.maintenanceSchedules?.map(() => false) ?? [];
      if (!isGroup) {
        results.push({
          key: groupKey,
          labelTitle,
          isGroup,
          children,
          values: tmpGroupValues,
        });
        continue;
      }

      for (const partKey of partKeys) {
        const childValue =
          groupValueByFirstMaintenanceScheduleItem.maintenance_parts[partKey];
        const childrenValues: boolean[] =
          props.data?.maintenanceSchedules?.map(
            (scheduleItem, scheduleIndex) => {
              const isEnable =
                scheduleItem.maintenance_part_groups[groupKey]
                  .maintenance_parts[partKey].enable &&
                scheduleItem.maintenance_part_groups[groupKey]
                  .maintenance_parts[partKey][
                  tableDisplay === 'full' ? 'show_on_main' : 'show_on_summary'
                ];
              if (isEnable) {
                tmpGroupValues[scheduleIndex] = isEnable;
              }
              return isEnable;
            }
          ) ?? [];
        // Force text display row. It won't care about enable logic
        const hasForceTextDisplay = !!props.data?.maintenanceSchedules?.some(
          (scheduleItem) =>
            scheduleItem.maintenance_part_groups[groupKey].maintenance_parts[
              partKey
            ].force_display_text
        );
        if (childrenValues.includes(true) || hasForceTextDisplay) {
          children.push({
            key: partKey,
            labelTitle: childValue.title,
            valueText: childValue.force_display_text,
            values: childrenValues,
          });
        }
      }
      if (tmpGroupValues.includes(true)) {
        results.push({
          key: groupKey,
          labelTitle,
          isGroup,
          children,
          values: tmpGroupValues,
        });
      }
    }

    return results;
  }, [props.data?.maintenanceSchedules, tableDisplay]);

  const onToggleFullTableDisplay = () => {
    if (onTableDisplayChange) {
      onTableDisplayChange(tableDisplay === 'full' ? 'brief' : 'full');
    }
  };

  return (
    <>
      <MaintenanceListTableHeader
        title={props.headerTitle ?? ''}
        isFullTable={tableDisplay === 'full'}
        toggleDataTrack="toggle-full-table-display"
        onToggleFullTableDisplay={onToggleFullTableDisplay}
      />
      <Box css={{ mt: '$space-6' }}>
        <Container size="desktop-only">
          {tableDisplay === 'full' ? (
            <MaintenanceListTableFull
              {...props.full}
              {...props.data}
              tableItems={filteredTableItems}
              tableDisplay={tableDisplay}
            />
          ) : (
            <MaintenanceListTableBrief
              {...props.brief}
              {...props.data}
              tableItems={filteredTableItems}
              tableDisplay={tableDisplay}
            />
          )}
        </Container>
      </Box>
    </>
  );
};

MaintenanceListTable.displayName = 'SectionMaintenanceListTable';
